import { useState, useEffect } from "react";
import { auth } from "../firebase";
import { signOut } from "firebase/auth";

const BASE = process.env.REACT_APP_API_URL || "http://localhost:8000";
const GCOLS = { pop: "#7adb2a", electronic: "#9b27af", rock: "#ff7a00" };

export default function AdminDashboard({ user }) {
  const [tab, setTab]             = useState("analytics");
  const [analytics, setAnalytics] = useState(null);
  const [comments, setComments]   = useState([]);
  const [milestones, setMilestones] = useState({});
  const [cmtFilter, setCmtFilter] = useState("all");
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    Promise.all([
      fetch(`${BASE}/admin/analytics`).then(r => r.json()),
      fetch(`${BASE}/admin/comments`).then(r => r.json()),
      fetch(`${BASE}/admin/milestones`).then(r => r.json()),
    ]).then(([a, c, m]) => {
      setAnalytics(a);
      setComments(c);
      setMilestones(m);
      setLoading(false);
    }).catch(err => {
      console.error("Admin load error:", err);
      setLoading(false);
    });
  }, []);

  async function moderate(id, status) {
    await fetch(`${BASE}/admin/comments/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    });
    setComments(cs => cs.map(c => c.id === id ? { ...c, status } : c));
  }

  async function deleteComment(id) {
    if (!window.confirm("Delete this comment permanently?")) return;
    await fetch(`${BASE}/admin/comments/${id}`, { method: "DELETE" });
    setComments(cs => cs.filter(c => c.id !== id));
  }

  async function handleSignOut() {
    await signOut(auth);
    window.location.href = "/";
  }

  const filteredComments = comments.filter(c =>
    cmtFilter === "all" || c.status === cmtFilter
  );
  const pendingCount = comments.filter(c => c.status === "pending").length;
  const maxPlays = analytics
    ? Math.max(...analytics.songs.map(s => s.play_count), 1)
    : 1;

  // ── Styles ──────────────────────────────────────────────────────────────
  const s = {
    wrap:        { maxWidth: 900, margin: "0 auto", padding: "32px 20px 60px", fontFamily: "'DM Sans', sans-serif" },
    header:      { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 32, paddingBottom: 20, borderBottom: "1px solid #eee" },
    title:       { fontSize: 22, fontWeight: 900, letterSpacing: "-.02em" },
    sub:         { fontSize: 11, color: "#999", letterSpacing: ".1em", textTransform: "uppercase", marginTop: 3 },
    headerRight: { display: "flex", gap: 10, alignItems: "center" },
    backLink:    { fontSize: 11, fontWeight: 700, color: "#ff2d9b", textDecoration: "none", letterSpacing: ".06em", textTransform: "uppercase" },
    signOut:     { fontSize: 11, fontWeight: 700, color: "#999", background: "none", border: "1px solid #eee", borderRadius: 3, padding: "6px 12px", cursor: "pointer", letterSpacing: ".06em", textTransform: "uppercase", fontFamily: "'DM Sans', sans-serif" },
    tabs:        { display: "flex", borderBottom: "2px solid #eee", marginBottom: 28 },
    statsGrid:   { display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 28 },
    statCard:    { background: "#fff", border: "1px solid #eee", borderRadius: 6, padding: 16 },
    statLabel:   { fontSize: 9, fontWeight: 700, letterSpacing: ".14em", textTransform: "uppercase", color: "#999", marginBottom: 6 },
    statVal:     { fontSize: 28, fontWeight: 900, letterSpacing: "-.02em", lineHeight: 1 },
    statSub:     { fontSize: 10, color: "#bbb", marginTop: 4 },
    sectionLabel:{ fontSize: 9, fontWeight: 700, letterSpacing: ".14em", textTransform: "uppercase", color: "#999", marginBottom: 12, marginTop: 24 },
    table:       { width: "100%", borderCollapse: "collapse", marginBottom: 8 },
    th:          { fontSize: 9, fontWeight: 700, letterSpacing: ".12em", textTransform: "uppercase", color: "#bbb", padding: "8px 12px", textAlign: "left", borderBottom: "1px solid #eee" },
    td:          { padding: "10px 12px", borderBottom: "1px solid #f5f5f5", fontSize: 13, verticalAlign: "middle" },
    filterBar:   { display: "flex", gap: 6, marginBottom: 16 },
  };

  if (loading) return (
    <div style={{ ...s.wrap, textAlign: "center", paddingTop: 80 }}>
      <p style={{ color: "#ccc", fontSize: 12, letterSpacing: ".1em", textTransform: "uppercase" }}>
        Loading analytics...
      </p>
    </div>
  );

  return (
    <div style={{ background: "#fafafa", minHeight: "100vh", backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.025'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")" }}>
    <div style={s.wrap}>

      {/* HEADER */}
      <div style={s.header}>
        <div>
          <div style={s.title}>
            SNHW <span style={{ color: "#ff2d9b" }}>Admin</span>
          </div>
          <div style={s.sub}>Signed in as {user.email}</div>
        </div>
        <div style={s.headerRight}>
          <a href="/" style={s.backLink}>← Back to site</a>
          <button style={s.signOut} onClick={handleSignOut}>Sign out</button>
        </div>
      </div>

      {/* TABS */}
      <div style={s.tabs}>
        {[
          { id: "analytics", label: "Analytics" },
          { id: "comments",  label: `Comments${pendingCount > 0 ? ` (${pendingCount} pending)` : ""}` },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            fontSize: 12, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase",
            padding: "10px 20px", border: "none", background: "transparent",
            cursor: "pointer", marginBottom: -2, fontFamily: "'DM Sans', sans-serif",
            color: tab === t.id ? "#ff2d9b" : "#999",
            borderBottom: `2px solid ${tab === t.id ? "#ff2d9b" : "transparent"}`,
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── ANALYTICS TAB ── */}
      {tab === "analytics" && analytics && (
        <>
          {/* STAT CARDS */}
          <div style={s.statsGrid}>
            {[
              { label: "Total Plays",       val: analytics.total_plays.toLocaleString(), sub: "All time",    color: "#111" },
              { label: "Songs",             val: analytics.songs.length,                  sub: "Published",  color: "#111" },
              { label: "Total Upvotes",     val: analytics.total_up,                      sub: "All songs",  color: "#7adb2a" },
              { label: "Pending Comments",  val: analytics.pending_comments,              sub: "Need review", color: pendingCount > 0 ? "#ff2d9b" : "#111" },
            ].map(({ label, val, sub, color }) => (
              <div key={label} style={s.statCard}>
                <div style={s.statLabel}>{label}</div>
                <div style={{ ...s.statVal, color }}>{val}</div>
                <div style={s.statSub}>{sub}</div>
              </div>
            ))}
          </div>

          {/* PLAY COUNT TABLE */}
          <div style={s.sectionLabel}>Play count per song</div>
          <table style={s.table}>
            <thead>
              <tr>
                {["Song", "Plays", "Upvotes", "Downvotes", "Score"].map((h, i) => (
                  <th key={h} style={{ ...s.th, textAlign: i > 1 ? "right" : "left" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {analytics.songs.map(song => {
                const col   = GCOLS[song.genre] || "#ff2d9b";
                const total = song.votes_up + song.votes_down;
                const score = total > 0 ? Math.round((song.votes_up / total) * 100) : 0;
                return (
                  <tr key={song.id} style={{ cursor: "default" }}>
                    <td style={s.td}>
                      <span style={{ fontWeight: 600 }}>{song.title}</span>
                      <span style={{
                        fontSize: 9, fontWeight: 700, letterSpacing: ".08em",
                        textTransform: "uppercase", padding: "2px 7px", borderRadius: 2,
                        marginLeft: 8, background: col + "22", color: col,
                      }}>{song.genre}</span>
                    </td>
                    <td style={s.td}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ flex: 1, height: 6, background: "#f0f0f0", borderRadius: 3, overflow: "hidden" }}>
                          <div style={{
                            height: "100%", background: col, borderRadius: 3,
                            width: `${(song.play_count / maxPlays * 100).toFixed(1)}%`,
                          }} />
                        </div>
                        <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#999", width: 40, textAlign: "right", flexShrink: 0 }}>
                          {song.play_count.toLocaleString()}
                        </span>
                      </div>
                    </td>
                    <td style={{ ...s.td, textAlign: "right", fontWeight: 700, color: "#7adb2a" }}>▲ {song.votes_up}</td>
                    <td style={{ ...s.td, textAlign: "right", fontWeight: 700, color: "#ff2d9b" }}>▼ {song.votes_down}</td>
                    <td style={{ ...s.td, textAlign: "right", fontWeight: 700, color: score > 60 ? "#7adb2a" : score > 30 ? "#ffd000" : "#999" }}>
                      {score}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* COMPLETION FUNNEL */}
          {milestones && Object.keys(milestones).length > 0 && (
            <>
              <div style={s.sectionLabel}>Completion funnel</div>
              <table style={s.table}>
                <thead>
                  <tr>
                    {["Song", "25%", "50%", "75%", "Complete"].map((h, i) => (
                      <th key={h} style={{ ...s.th, textAlign: i === 0 ? "left" : "right" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {analytics.songs.map(song => {
                    const m     = milestones[song.title] || {};
                    const plays = song.play_count || 1;
                    return (
                      <tr key={song.id}>
                        <td style={s.td}>
                          <span style={{ fontWeight: 600 }}>{song.title}</span>
                        </td>
                        {["25%", "50%", "75%", "complete"].map(key => {
                          const count = m[key] || 0;
                          const pct   = Math.round((count / plays) * 100);
                          return (
                            <td key={key} style={{ ...s.td, textAlign: "right" }}>
                              <span style={{
                                fontWeight: 700,
                                color: pct > 60 ? "#7adb2a" : pct > 30 ? "#ffd000" : pct > 0 ? "#ff2d9b" : "#ccc",
                              }}>
                                {pct}%
                              </span>
                              <span style={{ fontSize: 10, color: "#bbb", marginLeft: 4 }}>
                                ({count})
                              </span>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p style={{ fontSize: 10, color: "#ccc", marginTop: 8 }}>
                Funnel shows % of plays that reached each milestone. Data accumulates as users listen.
              </p>
            </>
          )}

          {milestones && Object.keys(milestones).length === 0 && (
            <p style={{ fontSize: 12, color: "#ccc", marginTop: 8, fontStyle: "italic" }}>
              No milestone data yet — funnel will appear once people start listening.
            </p>
          )}
        </>
      )}

      {/* ── COMMENTS TAB ── */}
      {tab === "comments" && (
        <>
          <div style={s.filterBar}>
            {["all", "pending", "approved", "rejected"].map(f => (
              <button key={f} onClick={() => setCmtFilter(f)} style={{
                fontSize: 10, fontWeight: 700, letterSpacing: ".06em",
                textTransform: "uppercase", padding: "5px 12px",
                border: `1.5px solid ${cmtFilter === f ? "#111" : "#eee"}`,
                background: cmtFilter === f ? "#111" : "transparent",
                color: cmtFilter === f ? "#fff" : "#999",
                cursor: "pointer", borderRadius: 2,
                fontFamily: "'DM Sans', sans-serif",
              }}>{f}</button>
            ))}
          </div>

          {filteredComments.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px", color: "#ccc", fontSize: 13 }}>
              No comments in this category.
            </div>
          ) : (
            filteredComments.map(c => {
              const statusColors = {
                pending:  { bg: "#fff8e0", color: "#b8860b" },
                approved: { bg: "#e8ffe0", color: "#2d7a00" },
                rejected: { bg: "#ffe0e0", color: "#b80000" },
              };
              const sc = statusColors[c.status] || statusColors.pending;
              return (
                <div key={c.id} style={{
                  background: "#fff",
                  border: "1px solid #eee",
                  borderLeft: `3px solid ${c.status === "approved" ? "#7adb2a" : c.status === "rejected" ? "#ff2d9b" : "#ffd000"}`,
                  borderRadius: 6, padding: 16, marginBottom: 10,
                  display: "grid", gridTemplateColumns: "1fr auto", gap: 12,
                  opacity: c.status === "pending" ? 1 : 0.75,
                }}>
                  <div>
                    <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "#bbb", marginBottom: 4 }}>
                      {c.song_id}
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#111", marginBottom: 3 }}>
                      {c.author || "Anonymous"}
                    </div>
                    <div style={{ fontSize: 13, color: "#555", lineHeight: 1.6 }}>
                      {c.text}
                    </div>
                    <div style={{ fontSize: 9, color: "#ccc", marginTop: 6, fontFamily: "'DM Mono', monospace" }}>
                      {c.created_at ? new Date(c.created_at).toLocaleString() : "—"}
                    </div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, flexShrink: 0 }}>
                    <span style={{
                      fontSize: 8, fontWeight: 700, letterSpacing: ".1em",
                      textTransform: "uppercase", padding: "2px 7px",
                      borderRadius: 2, background: sc.bg, color: sc.color,
                      textAlign: "center",
                    }}>
                      {c.status}
                    </span>
                    {c.status !== "approved" && (
                      <button onClick={() => moderate(c.id, "approved")} style={{
                        fontSize: 10, fontWeight: 700, letterSpacing: ".06em",
                        textTransform: "uppercase", padding: "6px 12px",
                        borderRadius: 3, border: "none", cursor: "pointer",
                        background: "#7adb2a", color: "#fff",
                        fontFamily: "'DM Sans', sans-serif",
                      }}>✓ Approve</button>
                    )}
                    {c.status !== "rejected" && (
                      <button onClick={() => moderate(c.id, "rejected")} style={{
                        fontSize: 10, fontWeight: 700, letterSpacing: ".06em",
                        textTransform: "uppercase", padding: "6px 12px",
                        borderRadius: 3, border: "none", cursor: "pointer",
                        background: "#eee", color: "#999",
                        fontFamily: "'DM Sans', sans-serif",
                      }}>✗ Reject</button>
                    )}
                    <button onClick={() => deleteComment(c.id)} style={{
                      fontSize: 10, fontWeight: 700, letterSpacing: ".06em",
                      textTransform: "uppercase", padding: "6px 12px",
                      borderRadius: 3, border: "1px solid #eee", cursor: "pointer",
                      background: "#fff", color: "#ccc",
                      fontFamily: "'DM Sans', sans-serif",
                    }}>🗑 Delete</button>
                  </div>
                </div>
              );
            })
          )}
        </>
      )}

    </div>
    </div>
  );
}